"use client";

import { useState } from "react";

/**
 * A copy button for the press kit's boilerplate.
 *
 * Not the dashboard's `CopyButton`: that one is styled with tokens scoped to
 * `.dashboard-root`, and importing it would couple a marketing page to the
 * dashboard's module. This is the same behaviour in the marketing palette.
 *
 * The text it copies is also on the page, selectable, so a denied clipboard
 * (an insecure context, a permissions prompt refused) loses nothing.
 */
export function CopyText({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      // The visible text changes to "Copied"; announcing it makes the change
      // reach a screen reader user too.
      aria-live="polite"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border-strong px-3 py-1.5 font-sans text-xs text-foreground transition-colors hover:bg-foreground/[0.05]"
    >
      <svg
        aria-hidden="true"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {copied ? (
          <path d="M20 6 9 17l-5-5" />
        ) : (
          <>
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </>
        )}
      </svg>
      {copied ? "Copied" : label}
    </button>
  );
}
